import { Close, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { Page, PageTitleWithBack, Button as SMButton, Loader } from '../../../../../components';
import {
  getJobTitleDetails,
  createJobTitle,
  updateJobTitle,
  getJobTitlePartCategories
} from '../../../../../fetch';
import { formatMoney } from '../../../../../helpers';
import { IUserJobTitle, PlaceOrderRoleType } from '../../../../../models';
import { CategoryListItem } from '../../../../production/manage-roles-detail/CategoryListItem';
import { DownloadCSV } from '../../../../production/manage-roles-detail/DownloadCSV';

interface IJobTitleDetailForm {}

export const JobTitleDetailForm: FC<IJobTitleDetailForm> = () => {
  const history = useHistory();
  const { id }: { id: string } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const [jobTitle, setJobTitle] = useState<IUserJobTitle | null>(null);
  const [isLoadingJobTitle, setLoadingJobTitle] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);

  const [displayName, setDisplayName] = useState('');
  const [isPlaceOrderRole, setIsPlaceOrderRole] = useState(false); // Default to No for new job titles
  const [materialSpendLimit, setMaterialSpendLimit] = useState('');
  const [actualMaterialSpendLimit, setActualMaterialSpendLimit] = useState<number>(0);
  const [changeOrderSpendLimit, setChangeOrderSpendLimit] = useState('');
  const [actualChangeOrderSpendLimit, setActualChangeOrderSpendLimit] = useState<number>(0);
  const [roleType, setRoleType] = useState<PlaceOrderRoleType | ''>('');
  const [allowsNeighborhoodConfiguration, setAllowsNeighborhoodConfiguration] =
    useState<boolean>(false);
  const [allowsTerritoryConfiguration, setAllowsTerritoryConfiguration] = useState<boolean>(false);

  // Disable fields if job title is in use
  const usersAssigned = jobTitle?.usersAssignedCount && jobTitle.usersAssignedCount > 0;
  const [roleCategories, setRoleCategories] = useState<any[] | null>(null);
  const [roleAssignments, setRoleAssignments] = useState<any>(null);
  const [partIds, setPartIds] = useState<number[]>([]);
  const [isLoadingCategories, setLoadingCategories] = useState<boolean>(false);

  const fetchPartCategories = useCallback(
    async (userJobTitleId: number) => {
      try {
        setLoadingCategories(true);
        const res = await getJobTitlePartCategories(userJobTitleId);
        setRoleCategories(res.hierarchy);
        setRoleAssignments(res.roleAssignments);
        setPartIds(res.roleAssignments?.flatMap((x: any) => x.approvedPartIds) ?? []);
      } catch (error) {
        enqueueSnackbar('Error loading part categories, please refresh and try again.', {
          variant: 'error'
        });
      } finally {
        setLoadingCategories(false);
      }
    },
    [enqueueSnackbar]
  );

  const fetchJobTitle = useCallback(async () => {
    try {
      setLoadingJobTitle(true);
      if (id !== 'add-title') {
        const jobTitleDetail = await getJobTitleDetails(Number(id));

        setJobTitle(jobTitleDetail);
        setDisplayName(jobTitleDetail.displayName);
        setIsPlaceOrderRole(jobTitleDetail.placeOrderRoleId !== null);
        setMaterialSpendLimit(formatMoney(jobTitleDetail.materialSpendLimit || 0, 0));
        setActualMaterialSpendLimit(jobTitleDetail.materialSpendLimit || 0);
        setChangeOrderSpendLimit(formatMoney(jobTitleDetail.changeOrderSpendLimit || 0, 0));
        setActualChangeOrderSpendLimit(jobTitleDetail.changeOrderSpendLimit || 0);
        setRoleType(jobTitleDetail.roleType || '');
        setAllowsNeighborhoodConfiguration(jobTitleDetail.allowsNeighborhoodConfiguration);
        setAllowsTerritoryConfiguration(jobTitleDetail.allowsTerritoryConfiguration);
        // Fetch part categories/assignments for edit mode
        fetchPartCategories(Number(id));
      }
    } catch (error) {
      enqueueSnackbar('Error loading job title, please refresh and try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingJobTitle(false);
    }
  }, [id, enqueueSnackbar, fetchPartCategories]);

  useEffect(() => {
    fetchJobTitle();
  }, [fetchJobTitle]);

  const title =
    id === 'add-title'
      ? `Add a New Job Title`
      : jobTitle
      ? `Edit Job Title${jobTitle.displayName ? ` - ${jobTitle.displayName}` : ''}`
      : 'Edit Job Title';

  const cannotDeleteRole = jobTitle?.canManipulatePlaceOrderRole === false;

  return (
    <Page title={title}>
      <PageTitleWithBack
        backText="Back to Job Titles"
        backUrl="/admin/user-tools/unified-user-management/manage-roles-and-titles/job-titles"
        title={title}
      />
      <div className={classes.content}>
        {isLoadingJobTitle && (
          <Box marginTop="16px">
            <Loader position="centered" type="inline" />
          </Box>
        )}
        {!isLoadingJobTitle && (
          <>
            <Grid container className={classes.jobTitleContainer} spacing={1}>
              <Grid className={classes.jobTitleItem} item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  name="displayName"
                  autoComplete="none"
                  label="Job Title"
                  value={displayName}
                  onChange={event => {
                    setDisplayName(event.target.value);
                  }}
                />
              </Grid>

              <Grid className={classes.jobTitleItem} item xs={12} sm={6}>
                <Tooltip
                  title={
                    cannotDeleteRole
                      ? 'The place order role cannot be removed while users are assigned to it.'
                      : ''
                  }
                  placement="top"
                  arrow
                  disableHoverListener={!cannotDeleteRole}
                  disableFocusListener={!cannotDeleteRole}
                  disableTouchListener={!cannotDeleteRole}
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isPlaceOrderRole}
                          onChange={e => setIsPlaceOrderRole(e.target.checked)}
                          name="isPlaceOrderRole"
                          color="primary"
                          disabled={cannotDeleteRole || usersAssigned}
                        />
                      }
                      label="Will the users place orders for construction materials?"
                    />
                  </span>
                </Tooltip>
              </Grid>

              <Grid className={classes.jobTitleItem} item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" size="small" disabled={usersAssigned}>
                  <InputLabel id="territory-filter-label">Filter Users for Territories</InputLabel>
                  <Select
                    labelId="territory-filter-label"
                    value={allowsTerritoryConfiguration}
                    onChange={e => setAllowsTerritoryConfiguration(e.target.value === 'true')}
                    label="Filter Users for Territories"
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {isPlaceOrderRole && (
                <>
                  <Grid className={classes.jobTitleItem} item xs={12} sm={6}>
                    <Tooltip
                      title={
                        !isPlaceOrderRole
                          ? 'Neighborhoods can only be configured on place order roles.'
                          : ''
                      }
                      placement="top"
                      arrow
                      disableHoverListener={isPlaceOrderRole}
                      disableFocusListener={isPlaceOrderRole}
                      disableTouchListener={isPlaceOrderRole}
                    >
                      <span>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          size="small"
                          disabled={!isPlaceOrderRole || usersAssigned}
                        >
                          <InputLabel id="neighborhood-filter-label">
                            Filter Users for Neighborhoods
                          </InputLabel>
                          <Select
                            labelId="neighborhood-filter-label"
                            value={allowsNeighborhoodConfiguration}
                            onChange={e =>
                              setAllowsNeighborhoodConfiguration(e.target.value === 'true')
                            }
                            label="Filter Users for Neighborhoods"
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                        </FormControl>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid className={classes.jobTitleItem} item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="materialSpendLimit"
                      autoComplete="none"
                      label="Materials Spend Limit"
                      value={materialSpendLimit}
                      onBlur={e => {
                        const re = /^[0-9\b]+$/;
                        const value = e.target.value
                          .replace('$', '')
                          .replace(',', '')
                          .replace('-', '');
                        if (value === '' || re.test(value)) {
                          setActualMaterialSpendLimit(Number(value));
                        }
                        setMaterialSpendLimit(formatMoney(e.target.value.replace('-', ''), 0));
                      }}
                      onChange={e => {
                        setMaterialSpendLimit(e.target.value);
                      }}
                    />
                  </Grid>

                  <Grid className={classes.jobTitleItem} item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="changeOrderSpendLimit"
                      autoComplete="none"
                      label="Change Order Spend Limit"
                      value={changeOrderSpendLimit}
                      onBlur={e => {
                        const re = /^[0-9\b]+$/;
                        const value = e.target.value
                          .replace('$', '')
                          .replace(',', '')
                          .replace('-', '');
                        if (value === '' || re.test(value)) {
                          setActualChangeOrderSpendLimit(Number(value));
                        }
                        setChangeOrderSpendLimit(formatMoney(e.target.value.replace('-', ''), 0));
                      }}
                      onChange={e => {
                        setChangeOrderSpendLimit(e.target.value);
                      }}
                    />
                  </Grid>

                  <Grid className={classes.jobTitleItem} item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      required
                      disabled={usersAssigned}
                    >
                      <InputLabel id="role-type-label">Role Type</InputLabel>
                      <Select
                        labelId="role-type-label"
                        value={roleType}
                        onChange={e => setRoleType(e.target.value as PlaceOrderRoleType | '')}
                        label="Job Group Type"
                      >
                        <MenuItem value="Foreman">Foreman</MenuItem>
                        <MenuItem value="Warranty">Warranty</MenuItem>
                        <MenuItem value="Superintendent">Superintendent</MenuItem>
                        <MenuItem value="ProjectManager">Project Manager</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>

            {id !== 'add' && jobTitle?.placeOrderRoleId !== null && isPlaceOrderRole && (
              <>
                <Divider style={{ margin: '24px 0' }} />
                <Stack
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={{ xs: 1, sm: 0 }}
                >
                  <Typography variant="h6" gutterBottom>
                    Select Assignments
                  </Typography>
                  <DownloadCSV
                    roleName={jobTitle?.displayName || displayName}
                    roleCategories={roleCategories}
                  />
                </Stack>
                {isLoadingCategories ? (
                  <Box marginTop="16px">
                    <Loader position="centered" type="inline" />
                  </Box>
                ) : (
                  <div style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {roleCategories &&
                      roleAssignments &&
                      roleCategories.length > 0 &&
                      roleCategories.map((cat, index) => (
                        <CategoryListItem
                          key={`CategoryListItem${index}`}
                          category={cat}
                          index={index}
                          roleCategories={roleCategories}
                          setRoleCategories={setRoleCategories}
                          setPartIds={setPartIds}
                          partIds={partIds}
                          roleAssignments={roleAssignments}
                          setRoleAssignments={setRoleAssignments}
                          roles={[{ id: jobTitle?.id, name: jobTitle?.displayName }]}
                        />
                      ))}
                  </div>
                )}
              </>
            )}
            <Grid container alignItems="center" className={classes.saveWrapper}>
              <ButtonGroup>
                <SMButton
                  id="save"
                  color="primary"
                  variant="contained"
                  disabled={
                    !displayName ||
                    (isPlaceOrderRole &&
                      (!materialSpendLimit || !changeOrderSpendLimit || !roleType))
                  }
                  loading={isSaving ? 'Saving...' : ''}
                  startIcon={<Save />}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const payload = {
                        name: displayName,
                        displayName,
                        materialsSpendLimit: isPlaceOrderRole ? actualMaterialSpendLimit : 0,
                        changeOrderSpendLimit: isPlaceOrderRole ? actualChangeOrderSpendLimit : 0,
                        allowsNeighborhoodConfiguration,
                        allowsTerritoryConfiguration,
                        placeOrderRoleType: isPlaceOrderRole
                          ? (roleType as PlaceOrderRoleType)
                          : ('' as PlaceOrderRoleType),
                        // Only include partIds on edit
                        ...(id !== 'add' && { partIds })
                      };

                      if (id === 'add') {
                        await createJobTitle(payload);
                        enqueueSnackbar('Job title created successfully!', { variant: 'success' });
                      } else {
                        await updateJobTitle(Number(id), payload);
                        enqueueSnackbar('Job title updated successfully!', { variant: 'success' });
                      }

                      history.push(
                        '/admin/user-tools/unified-user-management/manage-roles-and-titles?tab=job-titles'
                      );
                    } catch (error) {
                      const errorMessage =
                        error?.response?.data?.Detail || 'Error saving job title';
                      enqueueSnackbar(errorMessage, { variant: 'error' });
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Save
                </SMButton>
                <Button
                  id="cancel"
                  variant="contained"
                  color="inherit"
                  startIcon={<Close />}
                  onClick={() => {
                    history.push(
                      '/admin/user-tools/unified-user-management/manage-roles-and-titles?tab=job-titles'
                    );
                  }}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Grid>
          </>
        )}
      </div>
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    marginTop: theme.spacing(0.5)
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },
  jobTitleContainer: {
    marginTop: 8,
    marginBottom: theme.spacing(1)
  },
  jobTitleItem: {}
}));
